import { KeyRound, ShieldCheck, type LucideIcon } from 'lucide-react';
import type { KeyboardEvent } from 'react';

import { cn } from '@/lib/utils';

import type { LoginType } from '../types';

interface TabDefinition {
  id: LoginType;
  label: string;
  hint: string;
  icon: LucideIcon;
}

const TABS: readonly TabDefinition[] = [
  { id: 'admin', label: 'Administrador', hint: 'Correo y contraseña', icon: ShieldCheck },
  { id: 'operator', label: 'Operador', hint: 'Código de acceso', icon: KeyRound },
] as const;

interface LoginTabsProps {
  value: LoginType;
  onChange: (type: LoginType) => void;
  disabled?: boolean;
}

/**
 * Selector de tipo de acceso (control segmentado).
 * Implementa el patrón ARIA de tabs con navegación por flechas.
 */
export function LoginTabs({ value, onChange, disabled = false }: LoginTabsProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();

    const currentIndex = TABS.findIndex((tab) => tab.id === value);
    const offset = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (currentIndex + offset + TABS.length) % TABS.length;
    const nextTab = TABS[nextIndex];

    if (nextTab) {
      onChange(nextTab.id);
      document.getElementById(`login-tab-${nextTab.id}`)?.focus();
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Tipo de acceso"
      onKeyDown={handleKeyDown}
      className="grid grid-cols-2 gap-1 rounded-xl bg-ink-100/80 p-1"
    >
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = tab.id === value;

        return (
          <button
            key={tab.id}
            id={`login-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`login-panel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(tab.id)}
            title={tab.hint}
            className={cn(
              'flex cursor-pointer items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all duration-200',
              'disabled:cursor-not-allowed disabled:opacity-60',
              isActive
                ? 'bg-white text-brand-700 shadow-sm'
                : 'text-ink-500 hover:bg-white/60 hover:text-ink-800',
            )}
          >
            <Icon className="size-4" aria-hidden="true" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
